import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconDownload, IconPhotoScan, IconSparkles, IconMaximize } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import LimitReachedModal from '@/components/common/LimitReachedModal';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { useAuthStore } from '../../../../../store/authStore';
import { useOfficeSpaceStore } from '../../../../../store/officeSpaceStore';
import sohozToolsService from '../../../../../services/sohozToolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import useImagePaste from '../../components/image/useImagePaste';
import useFailureLock from '../../components/common/useFailureLock';
import { downloadBlob, getToolStatusText } from '../../toolPageUtils';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import Hint from '../../../../../components/common/Hint';
import ToolBoothTabs from '../../components/common/ToolBoothTabs';
import {
    AI_SPLIT_ACCEPT as ACCEPT,
    AI_SPLIT_MAX_BYTES as MAX_BYTES,
    fileToDataUrl,
    pieceToBlob,
} from './aiPhotoSplitEngine';

/**
 * The AI photo splitter: hand it one picture of several photo prints and it
 * finds each print and cuts it out on its own.
 *
 * The manual splitter (image/photo-split) is free and exact — you draw the
 * boxes. This one is the paid counterpart for when there are a dozen prints on
 * a table and drawing them all is the work: the model locates them, the server
 * cuts them at the source resolution and the pieces arrive ready to download.
 * One credit a run, spent the moment the button is pressed — a failed run or a
 * picture with nothing in it costs the same as a good one.
 */
export default function AiPhotoSplitImage() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const digits = useCallback((n) => (isBn ? toBnDigits(n) : String(n)), [isBn]);

    const { user } = useAuthStore();
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const isOperator = !!(selectedOfficeSpace && user && selectedOfficeSpace.ownerId != null
        && selectedOfficeSpace.ownerId !== user.id);

    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [pieces, setPieces] = useState([]);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [idleDragging, setIdleDragging] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(null); // open piece in the full-screen preview
    const [format, setFormat] = useState('original');
    const [creditCost, setCreditCost] = useState(null);
    const [progress, setProgress] = useState(0);
    const [limitOpen, setLimitOpen] = useState(false);

    // The badge on the run button: what this office space's owner pays a run.
    useEffect(() => {
        let alive = true;
        sohozToolsService
            .getCost(selectedOfficeSpace?.id ? { officeSpaceId: selectedOfficeSpace.id } : {})
            .then((res) => { if (alive) setCreditCost(res?.data?.creditCost ?? null); })
            .catch(() => {});
        return () => { alive = false; };
    }, [selectedOfficeSpace?.id]);

    const reset = useCallback(() => {
        setFile(null);
        setPreview(null);
        setPieces([]);
    }, []);

    const load = useCallback(async (picked) => {
        if (!picked) return;
        if (!picked.type?.startsWith('image/')) {
            notifications.show({ title: t('common.error'), message: t('aiPhotoSplit.errors.type'), color: 'red' });
            return;
        }
        if (picked.size > MAX_BYTES) {
            notifications.show({ title: t('common.error'), message: t('aiPhotoSplit.errors.tooLarge'), color: 'red' });
            return;
        }
        setFile(picked);
        setPieces([]);
        setPreview((old) => {
            if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
            return URL.createObjectURL(picked);
        });
    }, [t]);

    useImagePaste((pastedFiles) => load(pastedFiles?.[0]));

    useEffect(() => () => { if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);

    const lock = useFailureLock(3, 60); // 3 failed runs in a row → the run button waits a minute
    const run = useCallback(async () => {
        if (!file || busy || lock.locked) return;
        setBusy(true);
        try {
            const image = await fileToDataUrl(file);
            const res = await sohozToolsService.imageSplit({
                image,
                officeSpaceId: selectedOfficeSpace?.id || undefined,
            });
            const found = res?.data?.pieces || [];
            setPieces(found);
            lock.recordSuccess();
            notifications.show({
                title: t('aiPhotoSplit.title'),
                message: t('aiPhotoSplit.foundCount', { count: digits(found.length) }),
                color: 'green',
            });
        } catch (e) {
            if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true);
            else {
                lock.recordFailure();
                notifications.show({
                    title: t('common.error'),
                    message: e?.message || t('aiPhotoSplit.errors.failed'),
                    color: 'red',
                });
            }
        } finally {
            setBusy(false);
        }
    }, [file, busy, lock.locked, selectedOfficeSpace?.id, t, digits]);

    // Same as the reader: one call, no real progress, so the bar creeps and
    // waits at 90 rather than sitting frozen at a meaningless number.
    useEffect(() => {
        if (!busy) { setProgress(0); return undefined; }
        setProgress(6);
        const id = setInterval(() => {
            setProgress((p) => (p >= 90 ? p : p + Math.max(1, Math.round((90 - p) / 12))));
        }, 700);
        return () => clearInterval(id);
    }, [busy]);

    const baseName = useMemo(() => (file?.name || 'image').replace(/\.[^.]+$/, ''), [file]);

    // Before/after items for the shared compare viewer: "before" is the uploaded picture,
    // "after" is the cut-out piece.
    const compareItems = useMemo(() => pieces.map((p, i) => ({
        id: i,
        uploadedImage: preview || '',
        aiImage: p.dataUrl,
        fileName: `${baseName}-${i + 1}`,
        mimeType: 'image/jpeg',
    })), [pieces, preview, baseName]);

    const downloadOne = useCallback(async (index) => {
        const { blob, ext } = await pieceToBlob(pieces[index].dataUrl, format);
        if (blob) downloadBlob(blob, `${baseName}-${index + 1}.${ext}`);
    }, [pieces, format, baseName]);

    const downloadAll = useCallback(async () => {
        if (!pieces.length) return;
        setDownloading(true);
        try {
            if (pieces.length === 1) {
                await downloadOne(0);
            } else {
                const entries = [];
                for (let i = 0; i < pieces.length; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { blob, ext } = await pieceToBlob(pieces[i].dataUrl, format);
                    if (blob) entries.push({ name: `${baseName}-${i + 1}.${ext}`, blob });
                }
                downloadBlob(await zipImageFiles(entries), `${baseName}-photos.zip`);
            }
        } catch {
            notifications.show({ title: t('common.error'), message: t('aiPhotoSplit.errors.failed'), color: 'red' });
        } finally {
            setDownloading(false);
        }
    }, [pieces, downloadOne, format, baseName, t]);

    const status = busy ? 'processing' : !file ? 'empty' : pieces.length ? 'success' : 'ready';

    return (
        <>
            <ToolPageLayout
                slug="image/ai-photo-split"
                subtitleKey="aiPhotoSplit.subtitle"
                creditCost={creditCost}
                status={status}
                statusText={getToolStatusText(status, t, {
                    empty: t('aiPhotoSplit.status.empty'),
                    ready: t('aiPhotoSplit.status.ready'),
                    success: t('aiPhotoSplit.status.done', { count: digits(pieces.length) }),
                })}
                onReset={file ? reset : undefined}
                previewFirst
                showTrust={false}
                primaryAction={pieces.length ? {
                    label: pieces.length > 1 ? t('aiPhotoSplit.downloadZip') : t('aiPhotoSplit.download'),
                    onClick: downloadAll,
                    icon: IconDownload,
                    loading: downloading,
                } : {
                    label: busy ? t('aiPhotoSplit.working') : lock.locked ? t('toolPage.retryLocked', { s: digits(lock.secondsLeft) }) : t('aiPhotoSplit.run'),
                    disabled: lock.locked && !!file,
                    onClick: file ? run : () => fileInputRef.current?.click(),
                    icon: file ? IconSparkles : IconPhotoScan,
                    loading: busy,
                }}
                secondaryAction={pieces.length ? {
                    label: t('aiPhotoSplit.again'),
                    onClick: run,
                    icon: IconSparkles,
                    disabled: busy,
                } : undefined}
                settings={(
                    <div className="space-y-3">
                    <div className="space-y-3 rounded-[20px] border border-[#ECE8E4] bg-white p-4 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
                        <ToolBoothTabs onPick={load}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPT}
                            className="hidden"
                            onChange={(e) => { load(e.target.files?.[0]); e.target.value = ''; }}
                        />

                        <div className="space-y-4">
                            {pieces.length ? (
                                <section>
                                    <p className="mb-2.5 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                        {t('aiPhotoSplit.resultTitle')}
                                    </p>
                                    <div className="flex max-h-[204px] flex-col gap-1.5 overflow-y-auto pr-0.5 [scrollbar-width:thin]">
                                        {pieces.map((piece, index) => (
                                            <div
                                                key={`${index}-${piece.width}x${piece.height}`}
                                                className="flex items-center gap-2.5 rounded-xl border border-[#ECE8E4] p-1.5 transition-colors hover:border-primary/40 dark:border-zinc-700"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewIndex(index)}
                                                    aria-label={t('aiPhotoSplit.photoN', { n: digits(index + 1) })}
                                                    className="group relative h-[52px] w-[52px] flex-none overflow-hidden rounded-lg bg-white dark:bg-zinc-800"
                                                >
                                                    <img
                                                        src={piece.dataUrl}
                                                        alt=""
                                                        className="h-full w-full object-contain"
                                                    />
                                                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white/0 transition-all group-hover:bg-black/40 group-hover:text-white">
                                                        <IconMaximize size={16} />
                                                    </span>
                                                </button>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-100">
                                                        {t('aiPhotoSplit.photoN', { n: digits(index + 1) })}
                                                    </span>
                                                    <span className="block text-[11.5px] text-[#A8A099] dark:text-zinc-400">
                                                        {digits(piece.width)} × {digits(piece.height)}
                                                    </span>
                                                </span>
                                                <Hint label={t('aiPhotoSplit.download')}>
                                                    <button
                                                        type="button"
                                                        onClick={() => downloadOne(index)}
                                                        aria-label={t('aiPhotoSplit.download')}
                                                        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#A8A099] transition-colors hover:bg-[#F7F5F3] hover:text-primary dark:hover:bg-zinc-800"
                                                    >
                                                        <IconDownload size={15} stroke={1.8} />
                                                    </button>
                                                </Hint>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            <div className={pieces.length ? 'border-t border-[#F0EEEB] pt-4 dark:border-zinc-800' : ''}>
                                <OutputFormatPicker
                                    bare
                                    value={format}
                                    onChange={setFormat}
                                    transparentOutput
                                />
                            </div>
                        </div>
                        </ToolBoothTabs>
                    </div>
                    <div className="rounded-[20px] border border-[#ECE8E4] bg-white p-4 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
                        <p className="mb-2.5 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('aiPhotoSplit.howTitle')}
                        </p>
                        <p className="text-[12.5px] leading-relaxed text-[#A8A099] dark:text-zinc-400">
                            {t('aiPhotoSplit.howBody')}
                        </p>
                        {creditCost != null && (
                            <p className="mt-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-[12px] font-semibold leading-relaxed text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-400">
                                {t('aiPhotoSplit.creditNote', { count: digits(creditCost) })}
                            </p>
                        )}
                    </div>
                    </div>
                )}
                preview={(
                    <ToolPreviewPanel
                        title={t('aiPhotoSplit.stageTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        processingLabel={t('aiPhotoSplit.processingProgress', { progress: digits(progress) })}
                        progress={progress}
                        headerAction={file ? (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                {t('aiPhotoSplit.changePicture')}
                            </button>
                        ) : null}
                        onIdleClick={() => fileInputRef.current?.click()}
                        onIdleDrop={(e) => {
                            e.preventDefault();
                            setIdleDragging(false);
                            load(e.dataTransfer?.files?.[0]);
                        }}
                        onIdleDragOver={(e) => { e.preventDefault(); setIdleDragging(true); }}
                        onIdleDragLeave={() => setIdleDragging(false)}
                        idleDragging={idleDragging}
                    >
                        {preview ? (
                            <div className="p-3">
                                <img
                                    src={preview}
                                    alt=""
                                    className="mx-auto max-h-[560px] w-auto max-w-full rounded-xl object-contain"
                                />
                            </div>
                        ) : null}
                    </ToolPreviewPanel>
                )}
            />
            <LimitReachedModal opened={limitOpen} onClose={() => setLimitOpen(false)} isOperator={isOperator} />
            <ImagePreviewModal
                opened={previewIndex !== null}
                onClose={() => setPreviewIndex(null)}
                images={compareItems}
                initialImage={previewIndex !== null ? compareItems[previewIndex] : null}
                onDownloadImage={(img) => { const i = compareItems.findIndex((it) => it.id === img?.id); if (i >= 0) downloadOne(i); }}
                initialViewMode="side-by-side"
                useImageTitle
                hideEdit
                hidePrint
                hideShare
                hideCustomDownload
                hideDelete
            />
        </>
    );
}
